import { useMemo, useState, type ReactNode } from "react";
import { Search, GripVertical, ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FilterBuilder,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  TablePagination,
  MIN_COLUMN_WIDTH,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
} from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import type { Ticket } from "@/services/ticketsService";
import {
  STATUSES,
  categoryFilterOptions,
  TEAM_STYLE,
  PRIO_STYLE,
  normalizeCategory,
  statusMeta,
} from "./ticketConstants";
import { useSupportCategories } from "@/hooks/useSupportCategories";

interface ColumnDef {
  key: string;
  header: string;
  width: number;
  sortAccessor?: (row: Ticket) => string | number;
  render: (row: Ticket) => ReactNode;
}

function buildColumns(showTenant: boolean, routingEnabled: boolean): ColumnDef[] {
  const cols: ColumnDef[] = [
    {
      key: "title",
      header: "Title",
      width: 260,
      sortAccessor: (t) => t.title,
      render: (t) => <span className="font-medium text-foreground">{t.title}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: 140,
      sortAccessor: (t) => t.status,
      render: (t) => {
        const meta = statusMeta(t.status);
        return (
          <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold", meta.style)}>
            <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
            {meta.label}
          </span>
        );
      },
    },
    {
      key: "category",
      header: "Category / Team",
      width: 160,
      sortAccessor: (t) => normalizeCategory(t.category, routingEnabled),
      render: (t) => (
        <Badge variant="outline" className={cn("gap-1.5", TEAM_STYLE[t.category])}>
          {t.team}
        </Badge>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      width: 100,
      sortAccessor: (t) => t.priority,
      render: (t) => (
        <span className={cn("text-xs font-semibold", PRIO_STYLE[t.priority])}>
          {t.priority[0].toUpperCase() + t.priority.slice(1)}
        </span>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      width: 140,
      sortAccessor: (t) => t.assignee_name ?? "",
      render: (t) => t.assignee_name || <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: "requested_by",
      header: "Requested by",
      width: 180,
      sortAccessor: (t) => t.requested_by ?? "",
      render: (t) => t.requested_by || <span className="text-muted-foreground">—</span>,
    },
  ];

  if (showTenant) {
    cols.push({
      key: "tenant_id",
      header: "Tenant",
      width: 160,
      sortAccessor: (t) => t.tenant_id,
      render: (t) => <span className="truncate text-muted-foreground">{t.tenant_id}</span>,
    });
  }

  cols.push(
    {
      key: "created_at",
      header: "Created",
      width: 130,
      sortAccessor: (t) => t.created_at,
      render: (t) => new Date(t.created_at).toLocaleDateString(),
    },
    {
      key: "updated_at",
      header: "Updated",
      width: 130,
      sortAccessor: (t) => t.updated_at,
      render: (t) => new Date(t.updated_at).toLocaleDateString(),
    },
  );

  return cols;
}

/** Sortable + resizable + drag-to-reorder header cell. Built locally rather
 * than composed on top of the shared SortableTh (which has no ref/drag
 * passthrough) so the shared component stays untouched for its other
 * consumers (ManagementTable, WhatConvertsLeadsPage, AdHealthTable). */
function ReorderableTh({
  col,
  currentSortKey,
  sortDir,
  onSort,
  width,
  onResize,
}: {
  col: ColumnDef;
  currentSortKey: string | null;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  width: number;
  onResize: (key: string, event: React.PointerEvent<HTMLElement>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.key,
  });
  const active = currentSortKey === col.key;
  const caret = !col.sortAccessor ? null : active ? (
    sortDir === "asc" ? <ChevronUp className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />
  ) : (
    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
  );

  return (
    <TableHead
      ref={setNodeRef}
      style={{
        width,
        minWidth: MIN_COLUMN_WIDTH,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={cn("relative select-none hover:bg-muted/50", active && "text-foreground")}
    >
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          aria-label={`Drag to reorder ${col.header}`}
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <span
          className={cn("cursor-pointer", !col.sortAccessor && "cursor-default")}
          role={col.sortAccessor ? "button" : undefined}
          tabIndex={col.sortAccessor ? 0 : undefined}
          onClick={() => col.sortAccessor && onSort(col.key)}
          onKeyDown={(e) => {
            if (col.sortAccessor && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onSort(col.key);
            }
          }}
        >
          {col.header}
        </span>
        {caret}
      </span>
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Resize ${col.header} column`}
        className="absolute right-0 top-1/2 h-5 w-2 -translate-y-1/2 cursor-col-resize rounded hover:bg-teal/20"
        onPointerDown={(event) => onResize(col.key, event)}
        onClick={(event) => event.stopPropagation()}
      />
    </TableHead>
  );
}

export function TicketsTableView({
  tickets,
  isCrossTenant,
  onOpen,
}: {
  tickets: Ticket[];
  isCrossTenant: boolean;
  onOpen: (id: string) => void;
}) {
  const { routingEnabled } = useSupportCategories();
  const columns = useMemo(
    () => buildColumns(isCrossTenant, routingEnabled),
    [isCrossTenant, routingEnabled],
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(() => columns.map((c) => c.key));
  // Re-seed order if the column set itself changes (e.g. cross-tenant toggles
  // the Tenant column) — keeps any already-reordered keys, appends new ones.
  const orderedKeys = useMemo(() => {
    const known = new Set(columns.map((c) => c.key));
    const kept = columnOrder.filter((k) => known.has(k));
    const missing = columns.map((c) => c.key).filter((k) => !kept.includes(k));
    return [...kept, ...missing];
  }, [columns, columnOrder]);
  const orderedColumns = useMemo(
    () => orderedKeys.map((k) => columns.find((c) => c.key === k)!).filter(Boolean),
    [orderedKeys, columns],
  );

  const [query, setQuery] = useState("");
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>("and");

  const widthDefaults = useMemo(() => {
    const out: Record<string, number> = {};
    columns.forEach((c) => (out[c.key] = c.width));
    return out;
  }, [columns]);
  const { widths, startResize } = useColumnResize(widthDefaults);
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<Ticket>();

  const filterColumns: FilterColumn<Ticket>[] = useMemo(
    () => [
      { key: "title", label: "Title", type: "text", accessor: (t) => t.title },
      {
        key: "status",
        label: "Status",
        type: "enum",
        accessor: (t) => t.status,
        options: STATUSES.map((s) => ({ value: s.key, label: s.label })),
      },
      {
        key: "category",
        label: "Category",
        type: "enum",
        // Routing on: legacy technical rows match the Web Dev filter.
        accessor: (t) => normalizeCategory(t.category, routingEnabled),
        options: categoryFilterOptions(routingEnabled),
      },
      {
        key: "priority",
        label: "Priority",
        type: "enum",
        accessor: (t) => t.priority,
        options: [
          { value: "low", label: "Low" },
          { value: "medium", label: "Medium" },
          { value: "high", label: "High" },
        ],
      },
    ],
    [routingEnabled],
  );

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.requested_by ?? "").toLowerCase().includes(q) ||
        (t.assignee_name ?? "").toLowerCase().includes(q),
    );
  }, [tickets, query]);

  const filtered = useMemo(
    () => applyFilterRules(searched, rules, filterColumns, conjunction),
    [searched, rules, filterColumns, conjunction],
  );

  const sorted = useMemo(() => {
    const accessors: Record<string, (row: Ticket) => string | number> = {};
    columns.forEach((c) => {
      if (c.sortAccessor) accessors[c.key] = c.sortAccessor;
    });
    return sortRows(filtered, accessors);
  }, [filtered, sortRows, columns]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(sorted.length);
  const pageRows = slice(sorted);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const handleColumnDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    setColumnOrder((prev) => {
      const order = prev.length ? prev : orderedKeys;
      const oldIndex = order.indexOf(String(e.active.id));
      const newIndex = order.indexOf(String(e.over!.id));
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(order, oldIndex, newIndex);
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search tickets…"
            className="h-9 pl-8"
          />
        </div>
        <FilterBuilder<Ticket>
          columns={filterColumns}
          rules={rules}
          conjunction={conjunction}
          onRulesChange={(next) => {
            setRules(next);
            setPage(1);
          }}
          onConjunctionChange={setConjunction}
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableContext items={orderedKeys} strategy={horizontalListSortingStrategy}>
                  {orderedColumns.map((col) => (
                    <ReorderableTh
                      key={col.key}
                      col={col}
                      currentSortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                      width={widths[col.key] ?? col.width}
                      onResize={startResize}
                    />
                  ))}
                </SortableContext>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={orderedColumns.length} className="py-6 text-center text-sm text-muted-foreground">
                    {tickets.length === 0 ? "No tickets" : "Nothing matches this search or filter."}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => onOpen(row.id)}
                  >
                    {orderedColumns.map((col) => (
                      <TableCell key={col.key} className="text-xs">
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DndContext>
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
